import type { CookieCategory, CookieCategoryConsentPromiseResolver } from '../types'
import { consoleLog } from '@/tools/console-log'
import { glovoRum } from '@/tools/o11y/client/'

export const CONSENT_BANNER_EVENT = 'UC_UI_CMP_EVENT'

let isInitialized = false

/**
 *
 * A map that contains the promise resolvers that resolve the promises on the previous map
 * This map is populated when the consentPromiseMap is created.
 *
 */
const consentPromiseResolverMap: Record<CookieCategory, CookieCategoryConsentPromiseResolver> = {
  essential: () => {},
  functional: () => {},
  marketing: () => {},
}

/**
 *
 * A map that contains the promises that will be manually resolved, individually when the user
 *  accepts each one of the cookies categories.
 *
 */
const consentPromiseMap: Record<CookieCategory, Promise<boolean>> = {
  essential: new Promise((resolve) => (consentPromiseResolverMap.essential = resolve)),
  functional: new Promise((resolve) => (consentPromiseResolverMap.functional = resolve)),
  marketing: new Promise((resolve) => (consentPromiseResolverMap.marketing = resolve)),
}

// resolve category Essential promise immediately
consentPromiseResolverMap.essential(true)

/**
 *
 * Initializes the cookies blocking mechanism.
 * If cookies have already been configured resolves the necessary promises, if not, listens to the
 * consent banner events to react to the user's response.
 *
 */
export const initCookiesBlocker = async () => {
  if (isInitialized) {
    return
  } else {
    isInitialized = true
  }

  if (!__ucCmp) {
    throw new Error('[cookies-blocker]: Failed to init. UserCentrics CMP not found')
  }

  await validateCookiesCategories()
  const isPendingUserResponse = await __ucCmp.isConsentRequired()

  if (isPendingUserResponse) {
    window.addEventListener(CONSENT_BANNER_EVENT, onConsentBannerEvent as EventListener)
  } else {
    await evaluateCookieConsentStatus()
  }

  void consentPromiseMap.essential.then(() => consoleLog.green('Consent', 'Granted: essential'))
  void consentPromiseMap.functional.then(() => consoleLog.green('Consent', 'Granted: functional'))
  void consentPromiseMap.marketing.then(() => consoleLog.green('Consent', 'Granted: marketing'))
}

/**
 *
 * A function that returns a promise that resolves when the user accepts the cookies.
 * The promise resolves immediately if the user has already accepted the cookies.
 *
 */
export async function onCookiesAccepted(category: CookieCategory) {
  return consentPromiseMap[category]
}

/**
 *
 * Changes the language of the cookies consent banner contents.
 *
 */
export async function changeLanguage(language: string) {
  void __ucCmp.changeLanguage(language)
}

/**
 *
 * Resets the user consent selection, causing the banner to appear again next time user visits teh page.
 *
 */
export async function resetUserSelection() {
  void __ucCmp.clearUserSession()
}

/**
 *
 * A function that closes the cookies consent banner.
 *
 */
export function closeCookiesBanner() {
  void __ucCmp.closeCmp()
}

/**
 *
 * A function that shows the cookies consent details configuration popup.
 *
 */
export function openCookiesAdvancedConfigPopup() {
  __ucCmp.showSecondLayer()
}

/**
 *
 * Evaluates the user consent status for each one of the cookie categories, and resolves the
 * corresponding promises.
 *
 */
const evaluateCookieConsentStatus = async () => {
  const status = await getCookieConsentStatus()

  // NOTE: category Essential does not need to be evaluated, as is accepted by default

  if (status.functional) {
    consentPromiseResolverMap.functional(true)
  }
  if (status.marketing) {
    consentPromiseResolverMap.marketing(true)
  }
}

/**
 *
 * Returns an object with the current status of the user's consent for each one of the
 * cookies category
 *
 */
export const getCookieConsentStatus = async () => {
  const { categories } = await __ucCmp.getConsentDetails()

  return {
    essential: true,
    functional: categories.functional.state === 'ALL_ACCEPTED',
    marketing: categories.marketing.state === 'ALL_ACCEPTED',
  }
}

/**
 *
 * Validates that the required cookies categories are correctly defined.
 *
 */
const validateCookiesCategories = async () => {
  const { categories } = await __ucCmp.getConsentDetails()
  const { essential, functional, marketing } = categories
  if (!essential || !functional || !marketing) {
    glovoRum.addError(
      new Error(
        'Invalid cookies categories! Please review UserCentrics setup and ensure all categories are correctly defined (essential, functional, marketing)'
      ),
      { consentCategories: categories }
    )
  }
}

/**
 *
 * A function that listens to the consent banner events and reacts to the user's response by
 * triggering an evaluation of the user's consent status.
 *
 */
const onConsentBannerEvent = (event: CustomEvent<{ type: string }>) => {
  // NOTE: We need to wait a bit to ensure the consents are correctly propagated (Apparently when
  // the check is done immediately, the consents are not yet updated.)
  setTimeout(async () => {
    const supportedBannerEvents = ['ACCEPT_ALL', 'DENY_ALL', 'SAVE']
    const isSupportedBannerEvent = supportedBannerEvents.includes(event.detail.type)
    if (!isSupportedBannerEvent) {
      return
    }
    // there is no need to keep listening to these events as Content only occurs once
    window.removeEventListener(CONSENT_BANNER_EVENT, onConsentBannerEvent as EventListener)
    await evaluateCookieConsentStatus()
  }, 500)
}
